import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * RAF id for height updates
   * @type {number|undefined}
   */
  #heightRaf = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    window.addEventListener('resize', this.#handleResize, { passive: true });

    // Initial state
    this.current = this.#current;

    // Ensure correct height on first paint + after fonts load
    this.#updateHeight();
    if (document?.fonts?.ready) {
      document.fonts.ready.then(() => this.#updateHeight()).catch(() => {});
    }

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);

    if (this.#heightRaf) cancelAnimationFrame(this.#heightRaf);
    this.#heightRaf = undefined;
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    // Reset interval cleanly
    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    // ✅ Key fix: update container height to active slide height
    this.#updateHeight(relativeIndex);
  }

  /**
   * Keep height correct on resize (mobile address bar, orientation changes, etc.)
   */
  #handleResize = () => {
    this.#updateHeight();
  };

  /**
   * Updates the slideshow container height to prevent text clipping.
   * @param {number} [activeIndex]
   */
  #updateHeight(activeIndex) {
    const container = this.refs.slideshowContainer || this;

    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    // Find active slide either by index or aria-hidden
    const activeSlide =
      typeof activeIndex === 'number'
        ? slides[activeIndex]
        : slides.find((s) => s.getAttribute('aria-hidden') === 'false') || slides[0];

    if (!activeSlide) return;

    if (this.#heightRaf) cancelAnimationFrame(this.#heightRaf);

    this.#heightRaf = requestAnimationFrame(() => {
      // scrollHeight works well even if content wraps
      const height = activeSlide.scrollHeight || activeSlide.getBoundingClientRect().height;

      if (!height) return;

      // Use min-height so it can still grow naturally if theme allows it
      container.style.minHeight = `${Math.ceil(height)}px`;
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
